import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { startOfDay } from '../../utils/localDate';

export interface LineSeries {
  id: string;
  name: string;
  // an --am-<color> token: 'series-1' to 'series-8' tell agents apart,
  // 'done', 'cancelled' and 'failed' are the status colors of how executions ended
  color: string;
  // one value per bucket; null where the metric has no value (no executions)
  values: (number | null)[];
}

interface LineChartProps {
  title: string;
  // shown before the title, e.g. the status icon of a chart in a status color
  icon?: React.ReactNode;
  buckets: string[];
  stepSeconds: number;
  series: LineSeries[];
  formatValue: (value: number) => string;
  unit?: string;
  integerTicks?: boolean;
  emptyMessage: string;
}

const PLOT_HEIGHT = 200;
const MARGIN = { top: 12, right: 12, bottom: 28, left: 44 };
const END_LABEL_SPACE = 44;
const FALLBACK_WIDTH = 560;
const MAX_X_TICKS = 6;
// room one x label needs ("12 Oct", "Oct 26"), so narrow charts get fewer of them
const X_TICK_WIDTH = 64;
const MAX_DIRECT_LABELS = 4;
const MIN_LABEL_GAP = 14;
const DAY_SEC = 86400;

export const seriesColor = (color: string): string => `var(--am-${color})`;

// the short colored stroke that names a series in the legend and the tooltip
export const lineKeyClass = (color: string): string =>
  `agent-metrics-line-key agent-metrics-key-${color}`;

const compactNumber = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const niceStep = (rough: number): number => {
  const power = 10 ** Math.floor(Math.log10(rough));
  const fraction = rough / power;

  if (fraction <= 1) return power;
  if (fraction <= 2) return 2 * power;
  if (fraction <= 2.5) return 2.5 * power;
  if (fraction <= 5) return 5 * power;
  return 10 * power;
};

/** Clean ticks from zero to the first one at or above `max`. */
export const buildTicks = (max: number, integer = false): number[] => {
  if (!(max > 0)) return [0, 1];

  let step = niceStep(max / 4);
  if (integer) step = Math.max(1, Math.ceil(step));

  const count = Math.ceil(max / step - 1e-9);
  return Array.from({ length: count + 1 }, (_, index) => Number((index * step).toFixed(10)));
};

const localFormat = (options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-GB', options);

const timeFormat = localFormat({ hour: '2-digit', minute: '2-digit' });
const dayMonthFormat = localFormat({ day: 'numeric', month: 'short' });
const monthYearFormat = localFormat({ month: 'short', year: '2-digit' });
const fullDayFormat = localFormat({ day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Day and week buckets follow the period picked in local time, but they are fixed steps:
 * after a DST change they start an hour off midnight, and a period the server moved to a
 * UTC midnight starts hours off. Naming them after the nearest local midnight keeps the day.
 */
const bucketDate = (bucket: string, stepSeconds: number): Date => {
  const start = new Date(bucket);
  if (stepSeconds < DAY_SEC) return start;
  return startOfDay(new Date(start.getTime() + (DAY_SEC / 2) * 1000));
};

const formatTick = (bucket: string, stepSeconds: number, spansYears: boolean): string => {
  const date = bucketDate(bucket, stepSeconds);
  if (stepSeconds < DAY_SEC) return timeFormat.format(date);
  return spansYears ? monthYearFormat.format(date) : dayMonthFormat.format(date);
};

/** The period a bucket covers, as the tooltip and the table name it, in local time. */
export const describeBucket = (bucket: string, stepSeconds: number): string => {
  const date = bucketDate(bucket, stepSeconds);
  const day = fullDayFormat.format(date);

  if (stepSeconds < DAY_SEC) return `${day}, ${timeFormat.format(date)}`;
  if (stepSeconds === DAY_SEC) return day;
  return `Week of ${day}`;
};

const useWidth = (ref: RefObject<HTMLElement>): number => {
  const [width, setWidth] = useState(FALLBACK_WIDTH);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const next = Math.floor(entry.contentRect.width);
      if (next > 0) setWidth(next);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return width;
};

/** SVG path of a line that breaks wherever a value is missing. */
export const linePath = (
  values: (number | null)[],
  x: (index: number) => number,
  y: (value: number) => number,
): string => {
  let d = '';
  let isDrawing = false;

  values.forEach((value, index) => {
    if (value === null) {
      isDrawing = false;
      return;
    }
    d += `${isDrawing ? 'L' : 'M'}${x(index).toFixed(1)},${y(value).toFixed(1)}`;
    isDrawing = true;
  });

  return d;
};

const isIsolated = (values: (number | null)[], index: number): boolean =>
  values[index] !== null &&
  (index === 0 || values[index - 1] === null) &&
  (index === values.length - 1 || values[index + 1] === null);

export const LineChart: React.FC<LineChartProps> = ({
  title,
  icon,
  buckets,
  stepSeconds,
  series,
  formatValue,
  unit = '',
  integerTicks = false,
  emptyMessage,
}) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const width = useWidth(plotRef);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isTableView, setIsTableView] = useState(false);

  const count = buckets.length;
  const lastIndex = count - 1;

  const max = useMemo(
    () =>
      series.reduce(
        (current, item) =>
          item.values.reduce<number>(
            (highest, value) => (value !== null && value > highest ? value : highest),
            current,
          ),
        0,
      ),
    [series],
  );
  const hasValues = series.some((item) => item.values.some((value) => value !== null));
  const ticks = useMemo(() => buildTicks(max, integerTicks), [max, integerTicks]);
  const top = ticks[ticks.length - 1];

  const y = (value: number) => MARGIN.top + PLOT_HEIGHT * (1 - value / top);

  // the value at the end of a line, for a few lines and only where they do not collide
  const endLabels = useMemo(() => {
    if (series.length > MAX_DIRECT_LABELS || count === 0) return [];

    const labels = series
      .filter((item) => item.values[lastIndex] !== null && item.values[lastIndex] !== undefined)
      .map((item) => ({ id: item.id, value: item.values[lastIndex] as number }))
      .map((label) => ({ ...label, y: MARGIN.top + PLOT_HEIGHT * (1 - label.value / top) }))
      .sort((a, b) => a.y - b.y);

    const collides = labels.some(
      (label, index) => index > 0 && label.y - labels[index - 1].y < MIN_LABEL_GAP,
    );
    return collides ? [] : labels;
  }, [series, count, lastIndex, top]);

  const labeledIds = new Set(endLabels.map((label) => label.id));
  const right = MARGIN.right + (endLabels.length ? END_LABEL_SPACE : 0);
  const plotWidth = Math.max(width - MARGIN.left - right, 10);
  const x = (index: number) =>
    MARGIN.left + (count <= 1 ? plotWidth / 2 : (index * plotWidth) / (count - 1));

  const xTickCount = Math.max(2, Math.min(MAX_X_TICKS, Math.floor(plotWidth / X_TICK_WIDTH)));
  const tickEvery = Math.max(1, Math.ceil(count / xTickCount));
  const xTicks = Array.from({ length: count }, (_, index) => index).filter(
    (index) => index % tickEvery === 0,
  );
  const spansYears =
    count > 0 &&
    bucketDate(buckets[0], stepSeconds).getFullYear() !==
      bucketDate(buckets[lastIndex], stepSeconds).getFullYear();

  const indexAt = (clientX: number): number => {
    const rect = plotRef.current?.getBoundingClientRect();
    if (!rect || count <= 1) return 0;

    const position = ((clientX - rect.left - MARGIN.left) / plotWidth) * (count - 1);
    return Math.min(lastIndex, Math.max(0, Math.round(position)));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const moves: Record<string, (index: number) => number> = {
      ArrowLeft: (index) => Math.max(0, index - 1),
      ArrowRight: (index) => Math.min(lastIndex, index + 1),
      Home: () => 0,
      End: () => lastIndex,
    };

    if (event.key === 'Escape') {
      setActiveIndex(null);
      return;
    }

    const move = moves[event.key];
    if (!move) return;

    event.preventDefault();
    setActiveIndex((index) => move(index ?? lastIndex));
  };

  const formatWithUnit = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : `${formatValue(value)}${unit}`;

  const tooltipLeft = activeIndex !== null ? x(activeIndex) : 0;
  const isTooltipLeft = tooltipLeft > width * 0.6;

  return (
    <section className="agent-metrics-chart" aria-label={title}>
      <div className="agent-metrics-chart-header">
        <h4>
          {icon}
          {title}
        </h4>
        {hasValues && (
          <button
            type="button"
            className="agent-metrics-view-toggle"
            onClick={() => setIsTableView((current) => !current)}
          >
            {isTableView ? 'Chart' : 'Table'}
          </button>
        )}
      </div>

      {!hasValues && <div className="agent-metrics-chart-empty">{emptyMessage}</div>}

      {hasValues && isTableView && (
        <div className="agent-metrics-table-wrapper">
          <table className="agent-metrics-table" aria-label={title}>
            <thead>
              <tr>
                <th scope="col">Period</th>
                {series.map((item) => (
                  <th scope="col" key={item.id}>
                    {item.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {buckets.map((bucket, index) => (
                <tr key={bucket}>
                  <th scope="row">{describeBucket(bucket, stepSeconds)}</th>
                  {series.map((item) => (
                    <td key={item.id}>{formatWithUnit(item.values[index])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasValues && !isTableView && (
        <div
          ref={plotRef}
          className="agent-metrics-plot"
          tabIndex={0}
          role="group"
          aria-label={`${title}. Use the arrow keys to read the values of each period.`}
          onPointerMove={(event) => setActiveIndex(indexAt(event.clientX))}
          onPointerLeave={() => setActiveIndex(null)}
          onFocus={() => setActiveIndex((index) => index ?? lastIndex)}
          onBlur={() => setActiveIndex(null)}
          onKeyDown={handleKeyDown}
        >
          <svg width={width} height={MARGIN.top + PLOT_HEIGHT + MARGIN.bottom} aria-hidden="true">
            {ticks.map((tick) => (
              <g key={tick}>
                <line
                  className={tick === 0 ? 'agent-metrics-baseline' : 'agent-metrics-gridline'}
                  x1={MARGIN.left}
                  x2={MARGIN.left + plotWidth}
                  y1={y(tick)}
                  y2={y(tick)}
                />
                <text x={MARGIN.left - 8} y={y(tick)} dy="0.32em" textAnchor="end">
                  {compactNumber.format(tick)}
                </text>
              </g>
            ))}

            {xTicks.map((index) => (
              <text
                key={buckets[index]}
                x={x(index)}
                y={MARGIN.top + PLOT_HEIGHT + 18}
                textAnchor={count > 1 && index === 0 ? 'start' : 'middle'}
              >
                {formatTick(buckets[index], stepSeconds, spansYears)}
              </text>
            ))}

            {activeIndex !== null && (
              <line
                className="agent-metrics-crosshair"
                x1={x(activeIndex)}
                x2={x(activeIndex)}
                y1={MARGIN.top}
                y2={MARGIN.top + PLOT_HEIGHT}
              />
            )}

            {series.map((item) => (
              <path
                key={item.id}
                className="agent-metrics-line"
                d={linePath(item.values, x, y)}
                stroke={seriesColor(item.color)}
              />
            ))}

            {series.map((item) =>
              item.values.map((value, index) =>
                value !== null &&
                (isIsolated(item.values, index) ||
                  index === activeIndex ||
                  (index === lastIndex && labeledIds.has(item.id))) ? (
                  <circle
                    key={`${item.id}-${index}`}
                    className="agent-metrics-point"
                    cx={x(index)}
                    cy={y(value)}
                    r={4}
                    fill={seriesColor(item.color)}
                  />
                ) : null,
              ),
            )}

            {endLabels.map((label) => (
              <text key={label.id} x={x(lastIndex) + 8} y={label.y} dy="0.32em">
                {formatValue(label.value)}
              </text>
            ))}
          </svg>

          {activeIndex !== null && (
            <div
              className="agent-metrics-tooltip"
              role="status"
              style={
                isTooltipLeft
                  ? { left: tooltipLeft - 12, transform: 'translateX(-100%)' }
                  : { left: tooltipLeft + 12 }
              }
            >
              <div className="agent-metrics-tooltip-title">
                {describeBucket(buckets[activeIndex], stepSeconds)}
              </div>
              {series.map((item) => (
                <div key={item.id} className="agent-metrics-tooltip-row">
                  <span className={lineKeyClass(item.color)} />
                  <strong>{formatWithUnit(item.values[activeIndex])}</strong>
                  <span>{item.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default LineChart;
